/**
 * Date formatting utilities for travel itineraries.
 * Extracted from WanderAppComplete.jsx for shared use by web and mobile.
 *
 * IMPORTANT: parseLocalDate must be used for all YYYY-MM-DD string parsing
 * to avoid timezone-related off-by-one errors. new Date('2026-03-04') creates
 * a UTC midnight date, which can shift to the previous day in western timezones.
 */

/**
 * Parse a YYYY-MM-DD date string as a LOCAL date (not UTC).
 * This avoids the timezone bug where new Date('2026-03-04') creates a UTC midnight
 * date that shifts to Mar 3 in western timezones.
 *
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date|null} Local date object, or null if invalid
 */
export function parseLocalDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return new Date(year, month - 1, day); // month is 0-indexed in JS
}

/**
 * Calculate the number of days between two date strings (inclusive).
 * Uses parseLocalDate for timezone safety.
 *
 * @param {string} startDateStr - Start date in YYYY-MM-DD format
 * @param {string} endDateStr - End date in YYYY-MM-DD format
 * @returns {number|null} Number of days (inclusive), or null if invalid
 */
export function calculateDaysBetween(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return null;
  const start = parseLocalDate(startDateStr);
  const end = parseLocalDate(endDateStr);
  if (!start || !end) return null;
  const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days
  return diffDays > 0 ? diffDays : null;
}

/**
 * Format a date string for display (e.g., "Mar 4, 2026").
 * Uses parseLocalDate for timezone safety.
 *
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {string|null} Formatted date string, or null if invalid
 */
export function formatDateForDisplay(dateStr) {
  if (!dateStr) return null;
  const date = parseLocalDate(dateStr);
  if (!date) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format a date range as a human-readable string.
 * @param {string|Date} startDate - The trip start date (YYYY-MM-DD string or Date object)
 * @param {number} duration - Number of days
 * @returns {string} e.g., "Mar 3, 2026 - Mar 10, 2026"
 */
export function formatDateRange(startDate, duration) {
  if (!startDate || !duration) return '';
  // Use parseLocalDate for string inputs to avoid timezone bugs
  const start = typeof startDate === 'string' ? (parseLocalDate(startDate) || new Date(startDate)) : new Date(startDate);
  const end = new Date(start);
  end.setDate(end.getDate() + duration - 1);
  const options = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
}

/**
 * Get the formatted date string for a specific day of a trip.
 * @param {string|Date} startDate - The trip start date (YYYY-MM-DD string or Date object)
 * @param {number} dayNumber - 1-based day number
 * @returns {string} e.g., "Mon, Mar 3"
 */
export function getDateForDay(startDate, dayNumber) {
  if (!startDate || !dayNumber) return '';
  // Use parseLocalDate for string inputs to avoid timezone bugs
  const start = typeof startDate === 'string' ? (parseLocalDate(startDate) || new Date(startDate)) : new Date(startDate);
  const currentDate = new Date(start);
  currentDate.setDate(currentDate.getDate() + dayNumber - 1);
  const options = { weekday: 'short', month: 'short', day: 'numeric' };
  return currentDate.toLocaleDateString('en-US', options);
}
